import { exists, sortComparatorDesc } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import {
  compileConcept,
  getPrimaryButtonStyling,
  getSchemeForSchemelessComponent,
  getShadeKey,
} from 'base/styleguide/concept';
import {
  ColorVariable,
  CompiledChromaticColor,
  CompiledNeutralColor,
  CompiledScheme,
  ShadeKey,
} from 'base/styleguide/types';
import {
  Project,
  ProjectState,
  Section,
  SectionRef,
  SectionRefState,
  SectionState,
} from 'blocks/editor/model';
import { unrefSection } from 'blocks/editor/model_helpers';
import { isComponentSchemeless } from 'blocks/editor/styleguide/component_helpers';
import { ConceptState, UiStyle } from './model';

export const FloatingNavSlugs = [
  'navbar13_component',
  'navbar14_component',
  'navbar15_component',
];

export type NavbarType = 'floating-navbar' | 'navbar';

export const extractSectionMetadata = (
  library: LibraryCache,
  {
    sections,
    project,
    getScheme,
    concept,
    uiStyle,
    schemelessNavbarSchemeOverride,
  }: {
    sections: ReadonlyArray<SectionRef>;
    project: Project;
    getScheme: (elementId: string) => CompiledScheme;
    concept:
      | {
          colorVariables: ReadonlyMap<ColorVariable, string>;
          neutral: CompiledNeutralColor;
          chromatics: ReadonlyArray<CompiledChromaticColor>;
        }
      | undefined;
    uiStyle: UiStyle | undefined;
    schemelessNavbarSchemeOverride?: CompiledScheme;
  },
): ReadonlyArray<{
  sref: SectionRef;
  section: Section;
  scheme: CompiledScheme;
  navbarType?: NavbarType;
}> => {
  const darkAlternate =
    concept != null && uiStyle != null
      ? getPrimaryButtonStyling({ concept, uiStyle }).darkAlternate
      : false;

  const preProcessedSections = sections
    .map((sref) => {
      const section = unrefSection(sref, project);
      if (section?.element.current?.id == null) {
        return null;
      }

      const elementId = section.element.current.id;
      if (isComponentSchemeless(section.element.current.source.slug, library)) {
        return {
          sref: sref,
          section,
          scheme: getSchemeForSchemelessComponent({ darkAlternate }),
        };
      }

      const scheme = getScheme(elementId);

      return {
        sref: sref,
        section,
        scheme,
      };
    })
    .filter(exists);

  return preProcessedSections
    .map(({ sref, section, scheme }, index) => {
      if (section?.element.current?.id == null) {
        // This should never happen, but type narrowing isn't carried over
        return null;
      }

      const navbarType: NavbarType | undefined = FloatingNavSlugs.includes(
        section.element.current.source.slug,
      )
        ? 'floating-navbar'
        : section.element.current.source.slug.startsWith('navbar')
        ? 'navbar'
        : undefined;

      const nextSection = preProcessedSections[index + 1];

      if (navbarType === 'navbar') {
        if (schemelessNavbarSchemeOverride != null) {
          scheme = schemelessNavbarSchemeOverride;
        } else if (nextSection) {
          scheme = nextSection.scheme;
        }
      }

      return {
        sref,
        section,
        scheme,
        navbarType,
      };
    })
    .filter(exists);
};

export const extractSectionStateMetadata = (
  library: LibraryCache,
  {
    sections,
    project,
    getScheme,
    concept,
    uiStyle,
    schemelessNavbarSchemeOverride,
  }: {
    sections: ReadonlyArray<SectionRefState>;
    project: ProjectState;
    getScheme: (elementId: string) => CompiledScheme;
    concept:
      | {
          colorVariables: ReadonlyMap<ColorVariable, string>;
          neutral: CompiledNeutralColor;
          chromatics: ReadonlyArray<CompiledChromaticColor>;
        }
      | undefined;
    uiStyle: UiStyle | undefined;
    schemelessNavbarSchemeOverride?: CompiledScheme;
  },
): ReadonlyArray<{
  sref: SectionRefState;
  section: SectionState;
  scheme: CompiledScheme;
  navbarType?: NavbarType;
}> => {
  const darkAlternate =
    concept != null && uiStyle != null
      ? getPrimaryButtonStyling({ concept, uiStyle }).darkAlternate
      : false;

  const preProcessedSections = sections
    .map((sref) => {
      const section = unrefSection(sref, project);
      if (section?.element.current?.id == null) {
        return null;
      }

      const elementId = section.element.current.id;
      if (isComponentSchemeless(section.element.current.source.slug, library)) {
        return {
          sref: sref,
          section,
          scheme: getSchemeForSchemelessComponent({ darkAlternate }),
        };
      }

      const scheme = getScheme(elementId);

      return {
        sref: sref,
        section,
        scheme,
      };
    })
    .filter(exists);

  return preProcessedSections
    .map(({ sref, section, scheme }, index) => {
      if (section?.element.current?.id == null) {
        // This should never happen, but type narrowing isn't carried over
        return null;
      }

      const navbarType: NavbarType | undefined = FloatingNavSlugs.includes(
        section.element.current.source.slug,
      )
        ? 'floating-navbar'
        : section.element.current.source.slug.startsWith('navbar')
        ? 'navbar'
        : undefined;

      const nextSection = preProcessedSections[index + 1];

      if (navbarType === 'navbar') {
        if (nextSection) {
          scheme = nextSection.scheme;
        }
        if (scheme.shade < 0 && schemelessNavbarSchemeOverride != null) {
          scheme = schemelessNavbarSchemeOverride;
        }
      }

      return {
        sref,
        section,
        scheme,
        navbarType,
      };
    })
    .filter(exists);
};

/**
 * Given a list of sections, returns a list of unique shade keys used for
 * schemes in those sections, sorted from most to least frequently used in the
 * sections.
 *
 * The intention is that the output is used to generate scheme definitions on
 * export, with the first shade key returned becoming Scheme 1, and so on.
 */
export const getSchemeShadeKeys = ({
  library,
  sections,
  project,
  concept,
}: {
  library: LibraryCache;
  sections: ReadonlyArray<SectionRefState>;
  project: ProjectState;
  concept: ConceptState;
}): ReadonlyArray<ShadeKey> => {
  const compiledConcept = compileConcept(concept);
  const { schemes } = compiledConcept;

  const shadeKeysWithMetadata = extractSectionStateMetadata(library, {
    sections,
    project,
    getScheme: (elementId) =>
      schemes.find(
        (scheme) =>
          getShadeKey(scheme) ===
          concept.schemeOverrideByElementId.get(elementId),
      ) ?? schemes[0],
    concept: compiledConcept,
    uiStyle: concept.uiStyle,
  })
    .map(({ scheme, navbarType }) => {
      if (scheme.shade < 0) {
        // Skip schemeless sections
        return;
      }
      return { shadeKey: getShadeKey(scheme), navbarType };
    })
    .filter(exists);

  const shadeKeyFrequencies = shadeKeysWithMetadata.reduce<
    Record<string, number>
  >((acc, { shadeKey, navbarType }) => {
    // Don't include navbars in the shade key usage count.
    if (navbarType == null) {
      acc[shadeKey] = (acc[shadeKey] ?? 0) + 1;
    }
    return acc;
  }, {});

  const uniqueShadeKeys = [
    ...new Set(shadeKeysWithMetadata.map(({ shadeKey }) => shadeKey)),
  ];

  return uniqueShadeKeys.sort(
    sortComparatorDesc((shadeKey) => shadeKeyFrequencies[shadeKey] ?? 0),
  );
};

export const getDefaultScheme = ({
  schemeShadeKeys,
  schemes,
}: {
  schemeShadeKeys: ReadonlyArray<ShadeKey>;
  schemes: ReadonlyArray<CompiledScheme>;
}): CompiledScheme => {
  // Try to pick Color Scheme 1
  const colorScheme1 = schemes.find(
    (scheme) => getShadeKey(scheme) === schemeShadeKeys[0],
  );
  if (colorScheme1 != null) {
    return colorScheme1;
  }

  return (
    schemes[0] ?? getSchemeForSchemelessComponent({ darkAlternate: false })
  );
};
